// src/js/modules/navigation.js
// Scoped handlers for header navigation: search overlay and user-menu toggles.
// Idempotent initializer to avoid double-binding when old assets remain.

import { findAll, createObserverForSelector } from '../helpers/dom.js';

function initSearchOverlay() {
  const toggleSel = '[data-search-toggle], .search-toggle';
  const overlaySel = '[data-search-overlay], .search-overlay';
  const inputSel = '[data-search-input], .search-overlay input[type="search"], .search-input';
  const closeSel = '[data-search-close], .search-close';

  function attachHandlers(toggle, overlay) {
    if (!toggle || !overlay) return;
    if (toggle.__planc_search_bound) return; toggle.__planc_search_bound = true;

    const input = overlay.querySelector('input[type="search"]') || document.querySelector(inputSel);

    function open() {
      overlay.classList.add('is-open');
      overlay.setAttribute('aria-hidden', 'false');
      try { if (input && typeof input.focus === 'function') input.focus(); } catch (e) {}
    }

    function close() {
      overlay.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
    }

    toggle.addEventListener('click', (e) => { e.preventDefault(); open(); });

    const closeBtn = overlay.querySelector(closeSel);
    if (closeBtn) closeBtn.addEventListener('click', (e) => { e.preventDefault(); close(); });

    // close on overlay click outside content
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) close();
    });

    // close on ESC when overlay has focus
    overlay.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
  }

  const toggles = findAll(toggleSel);
  const overlays = findAll(overlaySel);
  if (toggles.length && overlays.length) {
    toggles.forEach(t => overlays.forEach(o => attachHandlers(t, o)));
    return;
  }

  // If not present yet, observe for insertions (short-lived observer)
  createObserverForSelector(toggleSel, () => initSearchOverlay(), { timeout: 4000 });
}

function initUserMenu() {
  const toggleSel = '[data-user-toggle], .user-toggle, .account-toggle';
  const menuSel = '[data-user-menu], .user-menu, .account-menu';

  function attach(toggle, menu) {
    if (!toggle || !menu) return;
    if (toggle.__planc_user_bound) return; toggle.__planc_user_bound = true;

    function open() { menu.classList.add('is-open'); toggle.setAttribute('aria-expanded', 'true'); }
    function close() { menu.classList.remove('is-open'); toggle.setAttribute('aria-expanded', 'false'); }

    toggle.addEventListener('click', (e) => {
      e.preventDefault(); if (menu.classList.contains('is-open')) { close(); } else { open(); }
    });

    // close when clicking outside
    document.addEventListener('click', (e) => {
      if (menu.classList.contains('is-open')) {
        if (!menu.contains(e.target) && !toggle.contains(e.target)) close();
      }
    });
  }

  const toggles = findAll(toggleSel);
  const menus = findAll(menuSel);
  if (toggles.length && menus.length) {
    toggles.forEach(t => menus.forEach(m => attach(t, m)));
    return;
  }
  createObserverForSelector(toggleSel, () => initUserMenu(), { timeout: 4000 });
}

export function initNavigation() {
  if (window.__planc_nav_initialized) return; window.__planc_nav_initialized = true;
  try { initSearchOverlay(); } catch (e) { if (window.PLANC_DEBUG && console) console.debug('planc: search init failed', e); }
  try { initUserMenu(); } catch (e) { if (window.PLANC_DEBUG && console) console.debug('planc: user-menu init failed', e); }
}

export default initNavigation;
